<template>
  <div class="c-integration-settings-tab">
    <div class="tab-settings">
      <component
        :is="component"
        ref="tabContent"
        @changed:integration-profile="trayState.isProfileSettingDirty = true"
      />
    </div>
    <IntegrationSettingsDrawerFooter
      :is-deleting="trayState.isDeleting"
      :is-saving="trayState.isSaving"
      :is-syncing="trayState.isSyncing"
      :save-button-text="saveButtonText"
      :disable-save-button="!canSave"
      @remove-integration="onRemoveIntegration"
      @save="onSaveTab"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, inject, useTemplateRef } from 'vue';

import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { TabValue } from '@/types/integrations';

import AccountObjectTab from './AccountObjectTab.vue';
import CustomObjectTab from './CustomObjectTab.vue';
import GeneralSettingsTab from './GeneralSettingsTab.vue';
import IntegrationSettingsDrawerFooter from './IntegrationSettingsDrawerFooter.vue';

const { activeTab } = defineProps<{
  activeTab: TabValue;
}>();

const emit = defineEmits<{
  'remove-integration': [];
}>();

const tabContent =
  useTemplateRef<InstanceType<typeof GeneralSettingsTab>>('tabContent');

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const component = computed(() => {
  switch (activeTab) {
    case 'general-settings':
      return GeneralSettingsTab;
    case 'account-object':
      return AccountObjectTab;
    case 'custom-object':
      return CustomObjectTab;
    default:
      return GeneralSettingsTab;
  }
});

const saveButtonText = computed(() => {
  switch (activeTab) {
    case 'general-settings':
      return 'Save General Settings';
    case 'account-object':
      return trayState.profileType === 'salesforce'
        ? 'Save Account Object'
        : 'Save Company Object';
    case 'custom-object':
      return 'Save Custom Object';
    default:
      return 'Save Settings';
  }
});

const hasTabNav = computed(
  () =>
    trayState.profileType === 'salesforce' ||
    trayState.profileType === 'hubspot',
);

const footerOffset = computed(() =>
  hasTabNav.value ? 'calc(100vh - 72px - 34px)' : 'calc(100vh - 72px)',
);

function onRemoveIntegration() {
  emit('remove-integration');
}

const canSave = computed(
  () =>
    trayState.hasTrayConfigChanges ||
    trayState.isPartnerFieldMappingsDirty ||
    trayState.isProfileSettingDirty,
);

async function onSaveTab() {
  if (activeTab === 'general-settings') {
    tabContent.value?.onSaveGeneralSettings();
    return;
  }

  await trayState.saveTabSettings(activeTab);
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-integration-settings-tab {
  @apply flex flex-col;
  height: v-bind(footerOffset) !important; /* Adjust for tabs and header */
}

.tab-settings {
  @apply overflow-auto flex-1;
}
</style>
